import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Typography,
  Grid,
  FormHelperText,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { getIn, useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { toast } from 'react-toastify';
import { InfoOutlined } from '@mui/icons-material';

import {
  FHAntdDateTimePicker,
  FBoxContainer,
  FHButton,
  FHInputField,
  FProgressBar,
  FHTooltip,
  FHSelect,
  FCheckbox,
} from '../../components/base';
import { providerActions } from '../../store';
import { disabledFutureDate, phoneNumberFormatter, cleanNumericInputValue } from '../../helpers';
import { trimsValuesInObject } from '../../utils/common';
import PageContainer from '../../components/layout/PageContainer';
import { BackgroundTypes, STANDARD_ERROR_MESSAGES } from '../../constants';
import {
  EmploymentTypeOptions,
  JobTitleOptions,
  ProviderTrainingStatusOptions,
  SupportedLanguages,
} from '../constants/general';
import { FORTA_BLUE, FORTA_BROWN, FORTA_WHITE } from '../../utils/colors';
import { ProviderStatusEnum } from '../../pages/constants';
import { normalizePreferredLanguage, normalizeSupportedLanguages } from './EditProviderForm.utils';

import type { AppDispatch, RootState } from '../../store';
import type { IOption, SelectValueType } from '../../utils/types';
import type { Moment } from 'moment';
import type { ChangeEvent } from 'react';

function EditProviderForm() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { providerId } = useParams();
  const { lastFetchedProvider: provider, loading: providersLoading } = useSelector(
    (state: RootState) => state.providers,
  );

  const { required, invalidValue, tenDigits } = STANDARD_ERROR_MESSAGES;

  useEffect(() => {
    if (!providerId) return;

    const providerRequest = dispatch(
      providerActions.getProviderById({ getAccessTokenSilently, providerId }),
    );

    return () => {
      providerRequest.abort();
      dispatch(providerActions.clearLastFetchedProvider());
    };
  }, [dispatch, getAccessTokenSilently, providerId]);

  const ProviderValidationSchema = Yup.object().shape({
    first_name: Yup.string().trim().required(required),
    last_name: Yup.string().trim().required(required),
    email: Yup.string().trim().email(invalidValue).required(required),
    sex: Yup.string().trim().required(required),
    dob: Yup.string().trim().required(required),
    employment_type: Yup.string().optional().nullable(),
    status: Yup.string().required(required),
    job_title: Yup.string().optional().nullable(),
    phone: Yup.string().trim().min(12, tenDigits).max(12, tenDigits).required(required),
    npi: Yup.string().optional(),
    certification: Yup.string().optional(),
    date_certification_start: Yup.string().optional().nullable(),
    date_certification_end: Yup.string().optional().nullable(),
    state_license: Yup.string().optional(),
    street_address_line_one: Yup.string().trim().required(required),
    street_address_city: Yup.string().trim().required(required),
    street_address_state: Yup.string().trim().required(required),
    preferred_language: Yup.string().oneOf(Object.values(SupportedLanguages)).optional().nullable(),
    supported_languages: Yup.array()
      .of(Yup.string().oneOf(Object.values(SupportedLanguages)))
      .optional()
      .nullable(),
    evv_required: Yup.boolean().required('Please select Yes or No'),
    street_address_zip: Yup.number()
      .typeError(invalidValue)
      .positive(invalidValue)
      .integer(invalidValue)
      .required(required),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: provider?.first_name || '',
      last_name: provider?.last_name || '',
      middle_name: provider?.middle_name || '',
      email: provider?.email || '',
      phone:
        provider && provider.phone
          ? provider.phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
          : '',
      dob: provider?.dob || moment().format('YYYY-MM-DD'),
      employment_type: provider?.employment_type || null,
      status: provider?.status || ProviderStatusEnum.NOT_TRAINING,
      job_title: provider?.job_title || null,
      sex: provider?.sex || '',
      caqh_id: provider?.caqh_id || '',
      payroll_id: provider?.payroll_id || '',
      date_certification_start: provider?.date_certification_start ?? undefined,
      date_certification_end: provider?.date_certification_end ?? undefined,
      street_address_line_one: provider?.street_address_line_one || '',
      street_address_line_two: provider?.street_address_line_two || '',
      street_address_city: provider?.street_address_city || '',
      street_address_state: provider?.street_address_state || '',
      street_address_zip: provider?.street_address_zip || '',
      preferred_language: normalizePreferredLanguage(provider?.preferred_language),
      supported_languages: normalizeSupportedLanguages(provider?.supported_languages),
      npi: provider?.npi || '',
      certification: provider?.certification || '',
      state_license: provider?.state_license || '',
      evv_required: provider?.evv_required ?? false,
    },

    onSubmit: async (values) => {
      if (!providerId) return;
      const trimedValues = trimsValuesInObject(values);
      const phone = cleanNumericInputValue(trimedValues['phone']);
      // EVV is admin-only: send it only when changed so a coordinator edit does not 403.
      if (values.evv_required === (provider?.evv_required ?? false)) {
        delete trimedValues.evv_required;
      }
      const updateResult = await dispatch(
        providerActions.updateProviderById({
          getAccessTokenSilently,
          providerId,
          data: {
            ...trimedValues,
            phone,
          },
        }),
      );

      if (providerActions.updateProviderById.fulfilled.match(updateResult)) {
        const toastId = 'profile-success';
        toast.success('Provider updated successfully', { toastId });
      } else {
        toast.error(updateResult.error.message);
      }
    },
    validationSchema: ProviderValidationSchema,
  });

  const { values, setFieldValue, errors, touched } = formik;

  const handleUpdateFieldValues = (key: string, value: SelectValueType) => {
    let formattedValue = value;
    if (key === 'phone') {
      formattedValue = phoneNumberFormatter(cleanNumericInputValue(String(value)));
    } else if (key === 'npi') {
      formattedValue = cleanNumericInputValue(String(value));
    }
    setFieldValue(key, formattedValue);
  };

  const handleSupportedLanguageChange = (language: SupportedLanguages, checked: boolean) => {
    const currentLanguages = values.supported_languages || [];
    if (checked) {
      // Add language if not already present
      if (!currentLanguages.includes(language)) {
        setFieldValue('supported_languages', [...currentLanguages, language]);
      }
    } else {
      // Remove language, but keep at least one language (English)
      if (currentLanguages.length > 1) {
        setFieldValue(
          'supported_languages',
          currentLanguages.filter((lang: SupportedLanguages) => lang !== language),
        );
        setFieldValue('preferred_language', SupportedLanguages.English);
      }
    }
  };

  if (!provider && providersLoading) {
    return <FProgressBar />;
  }
  return (
    <PageContainer>
      <Box sx={{ mt: 3 }}>
        <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
          <FBoxContainer dataTestId="editProviderForm" sx={{ borderRadius: '20px' }}>
            <Grid container>
              <Grid item md={12} lg={10} xl={8}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box
                    ml={1}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexGrow: 1,
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography textAlign="left" variant="h4">
                      Edit Provider
                    </Typography>
                    <Typography fontSize={14} textAlign="right" alignSelf={'flex-end'}>
                      <span style={{ color: 'red', fontSize: 16 }}>*</span>indicates mandatory
                      fields
                    </Typography>
                  </Box>
                  <Box
                    sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mt: 1, mb: 2 }}
                  />
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>
                        First Name<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        style={{ width: '100%', boxShadow: 'none' }}
                        value={values['first_name']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('first_name', e.target.value)
                        }
                        error={touched.first_name && Boolean(errors.first_name)}
                        helperText={touched.first_name && errors.first_name?.toString()}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>
                        Last Name<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        style={{ width: '100%', boxShadow: 'none' }}
                        value={values['last_name']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('last_name', e.target.value)
                        }
                        error={touched.last_name && Boolean(errors.last_name)}
                        helperText={touched.last_name && errors.last_name?.toString()}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>Middle Name:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        style={{ width: '100%', boxShadow: 'none' }}
                        value={values['middle_name']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('middle_name', e.target.value)
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>NPI:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        style={{ width: '100%', boxShadow: 'none' }}
                        value={values['npi']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('npi', e.target.value)
                        }
                        error={touched.npi && Boolean(errors.npi)}
                        helperText={formik.touched.npi && errors.npi?.toString()}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>Certification:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={{
                          'data-testid': 'editProviderCertification',
                        }}
                        style={{ width: '100%', boxShadow: 'none' }}
                        value={values['certification']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('certification', e.target.value)
                        }
                        error={touched.certification && Boolean(errors.certification)}
                        helperText={touched.certification && errors.certification?.toString()}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>Start date of certification:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHAntdDateTimePicker
                        inputHeight={40}
                        data-testid="certificationStartDate"
                        format="MM/DD/YYYY"
                        sx={{ boxShadow: 'none', width: '100%' }}
                        allowClear={false}
                        value={
                          values['date_certification_start']
                            ? moment(values['date_certification_start'])
                            : null
                        }
                        onSelect={(value: Moment) =>
                          handleUpdateFieldValues(
                            'date_certification_start',
                            value.format('YYYY-MM-DD'),
                          )
                        }
                        onChange={(value: Moment) =>
                          handleUpdateFieldValues(
                            'date_certification_start',
                            value.format('YYYY-MM-DD'),
                          )
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>End date of certification:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHAntdDateTimePicker
                        inputHeight={40}
                        data-testid="certificationEndDate"
                        format="MM/DD/YYYY"
                        sx={{ boxShadow: 'none', width: '100%' }}
                        allowClear={false}
                        value={
                          values['date_certification_end']
                            ? moment(values['date_certification_end'])
                            : null
                        }
                        onSelect={(value: Moment) =>
                          handleUpdateFieldValues(
                            'date_certification_end',
                            value.format('YYYY-MM-DD'),
                          )
                        }
                        onChange={(value: Moment) =>
                          handleUpdateFieldValues(
                            'date_certification_end',
                            value.format('YYYY-MM-DD'),
                          )
                        }
                      />
                    </Grid>
                  </Grid>

                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>Employment Type:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHSelect
                        data-testid="employment_type"
                        inputHeight={40}
                        backgroundType={BackgroundTypes.White}
                        fullWidth
                        clearable
                        options={EmploymentTypeOptions.map((employmentType: IOption) => ({
                          value: employmentType.label,
                          label: employmentType.label,
                        }))}
                        optionDataTestIdPrefix="employment_type"
                        value={values['employment_type']}
                        onUpdate={(value: SelectValueType) => {
                          handleUpdateFieldValues('employment_type', value);
                        }}
                        error={touched.employment_type && Boolean(errors.employment_type)}
                      />
                    </Grid>
                  </Grid>

                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>Job title:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHSelect
                        data-testid="job_title"
                        inputHeight={40}
                        backgroundType={BackgroundTypes.White}
                        fullWidth
                        clearable
                        options={JobTitleOptions.map((JobTitle: IOption) => ({
                          value: JobTitle.label,
                          label: JobTitle.label,
                        }))}
                        optionDataTestIdPrefix="job_title"
                        value={values['job_title']}
                        onUpdate={(value: SelectValueType) => {
                          handleUpdateFieldValues('job_title', value);
                        }}
                        error={touched.job_title && Boolean(errors.job_title)}
                      />
                    </Grid>
                  </Grid>

                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>State License:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        style={{ width: '100%', boxShadow: 'none' }}
                        value={values['state_license']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('state_license', e.target.value)
                        }
                      />
                    </Grid>
                  </Grid>

                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>Caqh Id:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        sx={{ boxShadow: 'none' }}
                        fullWidth
                        value={values['caqh_id']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('caqh_id', e.target.value)
                        }
                      />
                    </Grid>
                  </Grid>

                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>Payroll Id:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        sx={{ boxShadow: 'none' }}
                        fullWidth
                        value={values['payroll_id']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('payroll_id', e.target.value)
                        }
                      />
                    </Grid>
                  </Grid>

                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>
                        Email<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FHInputField
                          inputHeight={40}
                          sx={{
                            width: '100%',
                            color: 'black',
                            boxShadow: 'none',
                            flexGrow: 1,
                          }}
                          value={values['email']}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleUpdateFieldValues('email', e.target.value)
                          }
                          disabled
                          error={touched.email && Boolean(errors.email)}
                          helperText={touched.email && errors.email?.toString()}
                          coloredBackground
                        />
                        <FHTooltip
                          arrow
                          title={
                            <Typography>
                              To change email, use{' '}
                              <Link to="/admin/change-provider-email" style={{ color: 'inherit' }}>
                                Change Provider Email
                              </Link>
                            </Typography>
                          }
                          placement="top"
                          sx={{
                            borderRadius: '10px',
                            display: 'flex',
                            maxWidth: '350px',
                          }}
                          tooltipbackground={FORTA_WHITE}
                          tooltipBoxShadow={'0px 4px 4px rgba(0, 0, 0, 0.25)'}
                        >
                          <InfoOutlined sx={{ ml: 0.5, mr: -1, color: FORTA_BLUE.primary }} />
                        </FHTooltip>
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>
                        Phone<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        style={{ width: '100%', boxShadow: 'none' }}
                        value={values['phone']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('phone', e.target.value)
                        }
                        error={touched.phone && Boolean(errors.phone)}
                        helperText={touched.phone && errors.phone?.toString()}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>
                        DOB<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHAntdDateTimePicker
                        inputHeight={40}
                        format="MM/DD/YYYY"
                        style={{ width: '100%', boxShadow: 'none' }}
                        disabledDate={disabledFutureDate}
                        error={touched.dob && Boolean(errors.dob)}
                        helperText={touched.dob && required}
                        allowClear={false}
                        value={moment(values['dob'])}
                        onSelect={(value: Moment) =>
                          handleUpdateFieldValues('dob', value.format('YYYY-MM-DD'))
                        }
                        onChange={(value: Moment) =>
                          handleUpdateFieldValues('dob', value.format('YYYY-MM-DD'))
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>
                        Gender<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <RadioGroup
                        row
                        value={values['sex']}
                        name="gender-radio-buttons-group"
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('sex', e.target.value)
                        }
                      >
                        <FormControlLabel value={'male'} control={<Radio />} label="Male" />
                        <FormControlLabel value={'female'} control={<Radio />} label="Female" />
                      </RadioGroup>
                      {touched.sex && Boolean(errors.sex) && (
                        <FormHelperText sx={{ color: 'red', fontSize: '0.75rem' }}>
                          {errors.sex?.toString()}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>
                        Street Address 1<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        style={{ width: '100%', boxShadow: 'none' }}
                        value={values['street_address_line_one']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_line_one', e.target.value)
                        }
                        error={
                          touched.street_address_line_one && Boolean(errors.street_address_line_one)
                        }
                        helperText={
                          touched.street_address_line_one &&
                          errors.street_address_line_one?.toString()
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>Street Address 2:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        style={{ width: '100%', boxShadow: 'none' }}
                        value={values['street_address_line_two']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_line_two', e.target.value)
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>
                        City<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        style={{ width: '100%', boxShadow: 'none' }}
                        value={values['street_address_city']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_city', e.target.value)
                        }
                        error={touched.street_address_city && Boolean(errors.street_address_city)}
                        helperText={
                          touched.street_address_city && errors.street_address_city?.toString()
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>
                        State<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        style={{ width: '100%', boxShadow: 'none' }}
                        value={values['street_address_state']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_state', e.target.value)
                        }
                        error={touched.street_address_state && Boolean(errors.street_address_state)}
                        helperText={
                          touched.street_address_state && errors.street_address_state?.toString()
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>
                        Zip<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        style={{ width: '100%', boxShadow: 'none' }}
                        value={values['street_address_zip']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_zip', e.target.value)
                        }
                        error={touched.street_address_zip && Boolean(errors.street_address_zip)}
                        helperText={
                          touched.street_address_zip && errors.street_address_zip?.toString()
                        }
                      />
                    </Grid>
                    <Typography
                      textAlign={'left'}
                      mt={2}
                      ml={0.5}
                      color={FORTA_BROWN}
                      fontWeight={700}
                    >
                      Multi-language support is currently available only for guardians.
                    </Typography>
                    <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                      <Grid item xs={3.5}>
                        <Typography textAlign={'left'}>Supported Languages</Typography>
                      </Grid>
                      <Grid item xs={8.5}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {Object.values(SupportedLanguages).map((language) => {
                            const languageLabel =
                              language === SupportedLanguages.English ? 'English' : 'Spanish';
                            const isChecked =
                              values.supported_languages?.includes(language) || false;
                            return (
                              <FCheckbox
                                key={language}
                                label={languageLabel}
                                checked={isChecked}
                                sx={{
                                  '& .Mui-disabled': {
                                    opacity: 0.8,
                                    cursor: 'not-allowed',
                                  },
                                }}
                                disabled={language === SupportedLanguages.English}
                                onChange={(e) =>
                                  handleSupportedLanguageChange(language, e.target.checked)
                                }
                                data-testid={`supported-language-${language}`}
                              />
                            );
                          })}
                        </Box>
                      </Grid>
                    </Grid>
                    {values.supported_languages && values.supported_languages.length > 1 && (
                      <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                        <Grid item xs={3.5}>
                          <Typography textAlign={'left'}>Preferred Language</Typography>
                        </Grid>
                        <Grid item xs={8.5}>
                          <RadioGroup
                            data-testid="providerPreferredLanguage"
                            row
                            value={values['preferred_language']}
                            name="preferred-language-radio-buttons-group"
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              handleUpdateFieldValues('preferred_language', e.target.value)
                            }
                          >
                            <FormControlLabel
                              data-testid="providerPreferredLanguageEnglish"
                              value={SupportedLanguages.English}
                              control={<Radio />}
                              label="English"
                            />
                            <FormControlLabel
                              value={SupportedLanguages.Spanish}
                              control={<Radio />}
                              label="Spanish"
                            />
                          </RadioGroup>
                        </Grid>
                      </Grid>
                    )}
                    {/* Traning status */}
                    <Typography
                      textAlign={'left'}
                      mt={2}
                      ml={0.5}
                      color={FORTA_BROWN}
                      fontWeight={700}
                    >
                      Training status applies only to BCBAs and vBTs and is ignored for all other
                      roles.
                    </Typography>
                    <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                      <Grid item xs={3.5}>
                        <Typography textAlign={'left'}>Training Status:</Typography>
                      </Grid>
                      <Grid item xs={8.5}>
                        <FHSelect
                          data-testid="status"
                          inputHeight={40}
                          backgroundType={BackgroundTypes.White}
                          fullWidth
                          clearable
                          options={ProviderTrainingStatusOptions.map((employmentType: IOption) => ({
                            value: employmentType.id ?? '',
                            label: employmentType.label,
                          }))}
                          optionDataTestIdPrefix="status"
                          value={values['status']}
                          onUpdate={(value: SelectValueType) => {
                            handleUpdateFieldValues('status', value);
                          }}
                        />
                      </Grid>
                    </Grid>
                    <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                      <Grid item xs={3.5}>
                        <Typography textAlign={'left'}>Requires EVV:</Typography>
                      </Grid>
                      <Grid item xs={8.5}>
                        <RadioGroup
                          row
                          value={getIn(values, 'evv_required')}
                          name="required-evv-radio-group"
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setFieldValue('evv_required', e.target.value === 'true')
                          }
                        >
                          <FormControlLabel
                            data-testid="editProviderEvvEnabled"
                            value={true}
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            data-testid="editProviderEvvDisabled"
                            value={false}
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                        {getIn(touched, 'evv_required') && Boolean(getIn(errors, 'evv_required')) && (
                          <FormHelperText sx={{ color: 'red', fontSize: '0.75rem' }}>
                            {getIn(errors, 'evv_required')?.toString()}
                          </FormHelperText>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </FBoxContainer>
          <Box ml={0.5} mt={3} sx={{ display: 'flex' }}>
            <FHButton
              data-testid="editProviderFormSubmit"
              type="submit"
              disabled={formik.isSubmitting}
            >
              Submit
            </FHButton>
          </Box>
        </form>
      </Box>
    </PageContainer>
  );
}

export default EditProviderForm;
